import {
	DaysRestrictionDisneySearchFormInterface,
	DisneySearchFormSubmitInterface,
} from '@smartlinks/react-design-system/dist/src/lib/disney/shared/interfaces/disney-search-form.interface';
import { PassengersFieldRestrictionsInterface } from '@smartlinks/react-design-system/dist/src/shared/interfaces/generics/search-fields.interface';
import { DisneySearchFormUtil } from '@smartlinks/react-design-system/dist/src/lib/disney/shared/utils/disney-search-form.util';
import { DisneyLocalStorageInterface } from '../../shared/interfaces/widget-localstorage.interface';
import { DisneySearchWidgetProps } from '../../widgets/disney-search/disneySearchWidget';
import { DisneySearchFormSwitcher } from '@smartlinks/react-design-system';
import { DisneyService } from '../../shared/services/disney.service';
import { ProductType } from '../../shared/enums/product-types.enum';
import { DisneyUtil } from '../../shared/utils/disney-search.util';
import { Agency } from '../../shared/interfaces/agency.interface';
import { SourceType } from '../../shared/enums/source-type.enum';
import { CookieType } from '../../shared/enums/cookie-type.enum';
import { endPoints } from '../../shared/end-points/end-points';
import { getCookie } from '../../shared/utils/cookie.util';
import { useCallback, useMemo } from 'react';
import { TFunction } from 'react-i18next';

interface DisneySearchTemplateProps extends DisneySearchWidgetProps {
	disneyService: DisneyService;
	t: TFunction<'translation', undefined>;
	isNotHome?: boolean;
}

function DisneySearch({
	agency,
	disneyService,
	useExternalRedirect,
	useMinimunOnePassenger,
	t,
	callUrlSearch,
	callShowMobileFields,
	isNotHome,
}: DisneySearchTemplateProps): JSX.Element {
	const sourceType: string = getCookie(CookieType.source);
	const passengersRestrictions: PassengersFieldRestrictionsInterface = useMemo(
		() =>
			((tempAgency?: Agency | null): PassengersFieldRestrictionsInterface => {
				const maxDays: number = 20;
				return DisneyUtil.passengersRestrictions(maxDays, maxDays, useMinimunOnePassenger);
			})(agency),
		[agency],
	);

	const calendarRestriction: number = useMemo(
		() =>
			((tempAgency?: Agency | null): number => {
				return DisneyUtil.calendarRestriction(tempAgency);
			})(agency),
		[agency],
	);

	const daysRestriction: DaysRestrictionDisneySearchFormInterface = useMemo(
		() =>
			((): DaysRestrictionDisneySearchFormInterface => {
				return DisneyUtil.daysRestriction();
			})(),
		[],
	);

	const history: DisneySearchFormSubmitInterface | null = useMemo(
		() =>
			((tempDisneyService: DisneyService): DisneySearchFormSubmitInterface | null => {
				return isNotHome ? tempDisneyService.getDisneySearchFormHistory(t) : null;
			})(disneyService),
		[disneyService, t, isNotHome],
	);

	const searchHandler = useCallback(
		(data: DisneySearchFormSubmitInterface): void => {
			const dataForm: DisneySearchFormSubmitInterface = {
				...data,
				urlPath: DisneySearchFormUtil.mapSearchUrl(data),
			};

			const dataLocalStorage: DisneyLocalStorageInterface = {
				passengers: data.passengers,
				arrivalDate: data.arrivalDate,
				dates: data.dates,
				withPromoCode: data.withPromoCode,
				promoCode: data.promoCode,
			};

			if (useExternalRedirect) {
				const agencyDomainToRedirect: string = agency?.profile?.domainUrl || '';
				const createAnchor = document.createElement('a');
				createAnchor.target = '_blank';

				createAnchor.href = endPoints.flightsSearch(agencyDomainToRedirect, dataForm?.urlPath || '');

				createAnchor.click();

				return;
			}

			if (callUrlSearch != null) {
				callUrlSearch(dataForm);
			}

			disneyService.setLocalHistory(dataLocalStorage);
		},
		[disneyService, useExternalRedirect, agency, callUrlSearch],
	);

	const hasPromoCodeActive: boolean = (agency?.couponsConfig?.productsPromoCode || []).some(
		product => product.productType.toLowerCase() === ProductType.Disney.toLowerCase() && product.active,
	);

	return (
		<div className='flex w-full' data-testid='disney-search.template.container'>
			<DisneySearchFormSwitcher
				hasPromoCodeActive={hasPromoCodeActive}
				colorPrimary={agency?.lookAndFeel?.style?.primaryColor || '#000000'}
				colorSecondary={agency?.lookAndFeel?.style?.secondaryColor}
				customIcons={agency?.lookAndFeel?.customIcons}
				traductions={t}
				daysRestriction={daysRestriction}
				calendarRestriction={calendarRestriction}
				passengersRestrictions={passengersRestrictions}
				history={history}
				emitSubmitSearch={searchHandler}
				emitShowMobileFields={callShowMobileFields}
				useMinimunOnePassenger={useMinimunOnePassenger}
				widgetStyles={agency?.widgetStyles}
				isNotHome={isNotHome}
				sourceType={sourceType || SourceType.web}
				bannerType={agency?.lookAndFeel?.banner?.type}
			/>
		</div>
	);
}

export default DisneySearch;
